import { styled } from "@mui/material/styles";
import { Link } from "@tanstack/react-router";

import { MuiBreadcrumbs } from "../MuiComponents/MuiBreadcrumbs";
import { MuiTypography } from "../MuiComponents/MuiTypography";
import type { FileRouteTypes } from "@/routeTree.gen";
import type { ComponentProps } from "react";

/**
 * Valid navigation targets, derived from the generated route tree
 * (`src/routeTree.gen.ts`). Typing `to` against this union means typos and
 * removed routes fail to compile, even though `params` stays loosely typed.
 */
export type OrionRoutePath = FileRouteTypes["to"];

/** A clickable breadcrumb that navigates to a route. */
export type OrionBreadcrumbLink = {
  label: string;
  /** Target route path (e.g. `/dragon-tiles/$siteId`). */
  to: OrionRoutePath;
  params?: Record<string, string>;
  search?: Record<string, unknown>;
};

/** A static (non-clickable) breadcrumb, e.g. the current page. */
export type OrionBreadcrumbStatic = { label: string };

export type OrionBreadcrumbItem = OrionBreadcrumbLink | OrionBreadcrumbStatic;

const CrumbLink = styled(Link)(({ theme }) => ({
  textDecoration: "none",
  color: theme.palette.text.primary,
  "&:hover": { color: theme.palette.primary.main },
}));

function isLinkItem(item: OrionBreadcrumbItem): item is OrionBreadcrumbLink {
  return "to" in item && Boolean(item.to);
}

export type OrionBreadcrumbsProps = {
  items: OrionBreadcrumbItem[];
  "aria-label"?: string;
  "data-testid"?: string;
};

/**
 * Standardized breadcrumbs used across the platform.
 *
 * Items with a `to` render as clickable links (letting users navigate back up
 * the hierarchy); items without a `to` render as static text for the current
 * page. Items with an empty label are skipped, so no dangling separator is
 * rendered. Uses `/` as the universal separator.
 */
export function OrionBreadcrumbs({
  items,
  "aria-label": ariaLabel = "breadcrumb",
  "data-testid": dataTestId,
}: OrionBreadcrumbsProps) {
  const visibleItems = items.filter((item) => item.label !== "");

  return (
    <MuiBreadcrumbs
      separator="/"
      aria-label={ariaLabel}
      data-testid={dataTestId}
      sx={{ "& .MuiBreadcrumbs-separator": { color: "text.secondary" } }}
    >
      {visibleItems.map((item, index) => {
        const key = `${item.label}-${index}`;

        if (isLinkItem(item)) {
          // `to` is validated against the route tree via `OrionRoutePath`, but
          // TanStack's `Link` additionally correlates `params`/`search` to the
          // specific route — a relationship this generic list can't express, so
          // relax the prop object here. See `src/routeTree.gen.ts` for routes.
          const linkProps = {
            to: item.to,
            params: item.params,
            search: item.search,
          } as unknown as ComponentProps<typeof CrumbLink>;
          return (
            <CrumbLink key={key} {...linkProps}>
              <MuiTypography variant="body3" color="inherit">
                {item.label}
              </MuiTypography>
            </CrumbLink>
          );
        }

        return (
          <MuiTypography
            key={key}
            variant="body3"
            sx={{ color: "text.secondary" }}
          >
            {item.label}
          </MuiTypography>
        );
      })}
    </MuiBreadcrumbs>
  );
}
